import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { PricingPlan, CREDIT_CONFIG, getPlanById } from '../config/stripe';
import { BillingService, RedeemPromoCodeResult } from '../services/billingService';
import { supabase } from '../supabase/client';

// Use the Supabase UserBilling interface but maintain backward compatibility
interface UserBilling {
  userId: string;
  currentPlan: string;
  credits: number;
  creditsUsed: number;
  billingCycle: 'monthly' | 'yearly';
  subscriptionStatus: 'active' | 'inactive' | 'canceled' | 'past_due';
  subscriptionId?: string;
  currentPeriodEnd?: Date;
  nextBillingDate?: Date;
  subscriptionStartedAt?: Date;
}

interface BillingContextType {
  userBilling: UserBilling | null;
  loading: boolean;
  error: string | null;
  
  // Actions
  refreshBilling: () => Promise<void>;
  consumeCredits: (amount: number, description?: string) => Promise<boolean>;
  upgradeToStarter: () => Promise<string>; // Returns payment link
  createPaymentLink: (
    planId: string,
    billingCycle: 'monthly' | 'yearly',
    prefilledPromoCode?: string
  ) => Promise<string>;
  cancelSubscription: () => Promise<{
    success: boolean;
    refunded: boolean;
    refundAmount?: number;
    refundCurrency?: string;
    withinRefundWindow: boolean;
    error?: string;
  }>;
  redeemPromoCode: (rawCode: string) => Promise<RedeemPromoCodeResult>;
  // Anchor used by the UI to compute the 3-day money-back countdown.
  subscriptionStartedAt: Date | null;
  
  // Computed properties
  canRunAnalysis: boolean;
  remainingCredits: number;
  currentPlanDetails: PricingPlan | null;
  
  // Video Agent properties
  isVideoAgentUnlimited: boolean;
  isPaidUser: boolean;
  canCreateIndex: boolean;
  canUseChatQuery: () => Promise<boolean>;
  canUseIndexSearch: () => Promise<boolean>;
  consumeChatQuery: () => Promise<boolean>;
  consumeSearchQuery: () => Promise<boolean>;
  getVideoAgentUsage: () => Promise<{ chatQueriesUsed: number; searchQueriesUsed: number }>;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

export const useBilling = () => {
  const context = useContext(BillingContext);
  if (context === undefined) {
    throw new Error('useBilling must be used within a BillingProvider');
  }
  return context;
};

interface BillingProviderProps {
  children: ReactNode;
}

export const BillingProvider: React.FC<BillingProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [userBilling, setUserBilling] = useState<UserBilling | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ✅ FIXED: Extract transformation logic into pure function
  const transformBillingData = (data: any): UserBilling => {
    return {
      userId: data.user_id,
      currentPlan: data.current_plan,
      credits: data.credits,
      creditsUsed: data.credits_used,
      billingCycle: data.billing_cycle,
      subscriptionStatus: data.subscription_status,
      subscriptionId: data.subscription_id,
      currentPeriodEnd: data.current_period_end ? new Date(data.current_period_end) : undefined,
      nextBillingDate: data.next_billing_date ? new Date(data.next_billing_date) : undefined,
      subscriptionStartedAt: data.subscription_started_at ? new Date(data.subscription_started_at) : undefined,
    };
  };

  // Initialize billing data - wrapped in useCallback to stabilize reference
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // Note: userBilling intentionally excluded to prevent infinite loops
  const initializeBilling = React.useCallback(async (retryCount = 0) => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch billing data from Supabase with retry logic for new users
      let supabaseBilling = await BillingService.getUserBilling(currentUser.uid);
      
      if (supabaseBilling) {
        // ✅ Use transformation function
        const billing = transformBillingData(supabaseBilling);
        setUserBilling(billing);
        
        // ✅ CRITICAL: Backfill missing email from Firebase Auth
        // This fixes users whose email wasn't captured during registration
        if (!supabaseBilling.email && currentUser.email) {
          console.log('🔄 User missing email in billing, updating from Firebase...');
          try {
            await BillingService.updateBillingEmail(currentUser.uid, currentUser.email);
            console.log('✅ Email updated in billing record');
            supabaseBilling.email = currentUser.email;
          } catch (emailError) {
            console.warn('⚠️ Failed to update billing email (non-critical):', emailError);
          }
        }
        
        // ✅ CRITICAL: Backfill Stripe customer if missing
        // This ensures users who were created before Stripe integration 
        // or whose Stripe customer creation failed during signup get a customer
        if (!supabaseBilling.stripe_customer_id) {
          console.log('🔄 User missing Stripe customer, backfilling...');
          try {
            const backfillResult = await BillingService.backfillStripeCustomer(
              currentUser.uid,
              currentUser.email || supabaseBilling.email || undefined,
              currentUser.displayName || undefined
            );
            
            if (backfillResult.success) {
              console.log('✅ Stripe customer backfilled:', backfillResult.customerId);
              // Refresh billing data to get the updated stripe_customer_id
              supabaseBilling = await BillingService.getUserBilling(currentUser.uid);
              if (supabaseBilling) {
                setUserBilling(transformBillingData(supabaseBilling));
              }
            } else {
              console.warn('⚠️ Failed to backfill Stripe customer:', backfillResult.error);
            }
          } catch (backfillError) {
            console.warn('⚠️ Error backfilling Stripe customer (non-critical):', backfillError);
            // Don't throw - billing still works, just Stripe payments won't
          }
        }
      } else {
        // Initialize new user billing
        console.log('💳 Initializing billing for new user...');
        const newBilling = await BillingService.initializeUserBilling(
          currentUser.uid, 
          currentUser.email || undefined
        );
        
        // ✅ Use transformation function
        const billing = transformBillingData(newBilling);
        setUserBilling(billing);
        console.log('✅ New user billing initialized with credits:', billing.credits);
        
        // ✅ Also create Stripe customer for new users
        console.log('🔄 New billing initialized, creating Stripe customer...');
        try {
          const backfillResult = await BillingService.backfillStripeCustomer(
            currentUser.uid,
            currentUser.email || undefined,
            currentUser.displayName || undefined
          );
          
          if (backfillResult.success) {
            console.log('✅ Stripe customer created for new user:', backfillResult.customerId);
          } else {
            console.warn('⚠️ Failed to create Stripe customer for new user:', backfillResult.error);
          }
        } catch (backfillError) {
          console.warn('⚠️ Error creating Stripe customer (non-critical):', backfillError);
        }
      }

    } catch (err: any) {
      // Handle duplicate key errors gracefully - this is common during race conditions
      // when AuthService and BillingContext both try to create billing simultaneously
      const isDuplicateKey = err?.code === '23505' || err?.code === 23505 ||
                             err?.message?.includes('duplicate key') ||
                             err?.message?.includes('unique constraint');
      
      if (isDuplicateKey) {
        console.log('ℹ️ Billing race condition detected, fetching existing record...');
        // Try to fetch existing record - it was created by another process
        try {
          const { data: existingData, error: fetchError } = await supabase
            .from('user_billing')
            .select('*')
            .eq('user_id', currentUser.uid)
            .single();
          
          if (!fetchError && existingData) {
            const billing = transformBillingData(existingData);
            setUserBilling(billing);
            console.log('✅ Retrieved existing billing after race condition:', {
              credits: billing.credits,
              plan: billing.currentPlan
            });
            return; // Success - exit early
          }
        } catch (fetchErr) {
          console.warn('Could not fetch existing billing after race condition:', fetchErr);
        }
      } else {
        console.error('Error initializing billing:', err);
        setError('Failed to load billing information. Please refresh the page.');
      }
      
      // Fallback to localStorage for development
      const storedBilling = localStorage.getItem(`billing_${currentUser?.uid}`);
      if (storedBilling && !userBilling) {
        const billing = JSON.parse(storedBilling);
        setUserBilling(billing);
        setError('Using offline billing data');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Initialize billing when user changes
  useEffect(() => {
    if (currentUser) {
      console.log('👤 User authenticated, initializing billing...', {
        uid: currentUser.uid,
        email: currentUser.email
      });
      initializeBilling();
    } else {
      console.log('👤 No user, clearing billing state');
      setUserBilling(null);
    }
  }, [currentUser, initializeBilling]); // ✅ All dependencies included

  const saveBillingToStorage = (billing: UserBilling) => {
    localStorage.setItem(`billing_${billing.userId}`, JSON.stringify(billing));
  };

  const refreshBilling = async () => {
    if (!currentUser) return;
    await initializeBilling();
  };

  const redeemPromoCode = React.useCallback(
    async (rawCode: string): Promise<RedeemPromoCodeResult> => {
      if (!currentUser) {
        return {
          ok: false,
          code: 'invalid_user',
          message: 'Please sign in to redeem a code.',
        };
      }
      const result = await BillingService.redeemPromoCode(currentUser.uid, rawCode);
      if (result.ok) {
        await initializeBilling();
      }
      return result;
    },
    [currentUser, initializeBilling]
  );

  const consumeCredits = async (amount: number, description: string = 'Video analysis'): Promise<boolean> => {
    // Allow credit consumption for authenticated users, even if billing data hasn't loaded yet
    // New users should have free credits
    if (!currentUser) return false;

    try {
      // Use Supabase billing service to consume credits
      const success = await BillingService.consumeCredits(
        currentUser.uid, 
        amount, 
        description
      );

      if (success) {
        // ✅ FIXED: Fetch fresh billing data after successful consumption
        const updatedBilling = await BillingService.getUserBilling(currentUser.uid);
        if (updatedBilling) {
          // ✅ Use transformation function to ensure consistent state
          const billing = transformBillingData(updatedBilling);
          setUserBilling(billing);

          // Also save to localStorage as backup
          saveBillingToStorage(billing);
        }
        setError(null);
        return true;
      } else {
        setError('Insufficient credits. Please upgrade your plan.');
        return false;
      }
    } catch (err) {
      console.error('Error consuming credits:', err);
      setError('Failed to consume credits. Please try again.');

      // Dev-only offline fallback — never fake a debit in production (MN-UX-003).
      if (
        process.env.NODE_ENV === 'development' &&
        userBilling &&
        userBilling.credits >= amount
      ) {
        const updatedBilling = {
          ...userBilling,
          credits: userBilling.credits - amount,
          creditsUsed: userBilling.creditsUsed + amount,
        };
        setUserBilling(updatedBilling);
        saveBillingToStorage(updatedBilling);
        setError('Using offline billing mode');
        return true;
      }

      return false;
    }
  };

  const createPaymentLink = React.useCallback(
    async (
      planId: string,
      billingCycle: 'monthly' | 'yearly',
      prefilledPromoCode?: string
    ): Promise<string> => {
      if (!currentUser) {
        throw new Error('Sign in required to upgrade');
      }
      // Get the base payment link for the plan
      const plan = getPlanById(planId);
      if (!plan?.paymentLink) {
        throw new Error(`Payment link not found for plan: ${planId}`);
      }

      const baseUrl = window.location.origin;
      const successUrlConfigured = `${baseUrl}/payment-success?session_id={CHECKOUT_SESSION_ID}&plan=${encodeURIComponent(planId)}&billing_cycle=${encodeURIComponent(billingCycle)}`;
      const cancelUrl = `${baseUrl}/pricing`;

      console.log('Payment link construction:', {
        planId,
        billingCycle,
        baseUrl,
        successUrlStripeDashboard: successUrlConfigured,
        cancelUrl,
        basePaymentLink: plan.paymentLink,
      });

      console.log(
        '[billing] In Stripe Dashboard, set Payment Link success URL to (copy):',
        successUrlConfigured
      );
      console.log('[billing] Stripe Payment Link cancel URL (reference):', cancelUrl);

      const paymentUrl = new URL(plan.paymentLink);
      paymentUrl.searchParams.set('plan', planId);
      paymentUrl.searchParams.set('billing_cycle', billingCycle);
      paymentUrl.searchParams.set('client_reference_id', currentUser.uid);
      const emailTrim = currentUser.email?.trim();
      if (emailTrim) {
        paymentUrl.searchParams.set('prefilled_email', emailTrim);
      }
      const promo = prefilledPromoCode?.trim();
      if (promo) {
        paymentUrl.searchParams.set('prefilled_promo_code', promo);
      }

      const finalUrl = paymentUrl.toString();
      console.log('Final payment URL (Stripe attrs):', finalUrl);

      return finalUrl;
    },
    [currentUser]
  );

  const upgradeToStarter = async (): Promise<string> => {
    return await createPaymentLink('starter', 'monthly');
  };

  const cancelSubscription = async () => {
    if (!currentUser) {
      return {
        success: false,
        refunded: false,
        withinRefundWindow: false,
        error: 'Not signed in',
      };
    }

    const result = await BillingService.cancelSubscription(currentUser.uid);
    if (result.success) {
      // Pull the fresh billing row so the UI immediately reflects the
      // downgrade to free + 0 credits.
      await initializeBilling();
    }
    return result;
  };

  // Computed properties
  const canRunAnalysis = userBilling ? (
    // Check for unlimited plans first
    (userBilling.currentPlan === 'pro' || userBilling.currentPlan === 'enterprise') ?
      userBilling.subscriptionStatus === 'active' :
      // For credit-based plans, check if user has enough credits
      userBilling.credits >= CREDIT_CONFIG.costPerRun
  ) : (
    // Don't allow analysis until billing data is loaded
    // This prevents free users from running analysis multiple times
    false
  );

  const remainingCredits = userBilling ? userBilling.credits : (
    // For new users without billing data, show default free credits
    currentUser ? 1 : 0
  );

  const currentPlanDetails = userBilling ? getPlanById(userBilling.currentPlan) || null : null;

  // Video Agent computed properties
  const isVideoAgentUnlimited = userBilling ? (
    userBilling.credits > CREDIT_CONFIG.videoAgent.unlimitedThreshold ||
    (userBilling.subscriptionStatus === 'active' && 
     (userBilling.currentPlan === 'pro' || userBilling.currentPlan === 'enterprise' || userBilling.currentPlan === 'starter'))
  ) : false;

  const isPaidUser = userBilling ? (
    userBilling.currentPlan !== 'free'
  ) : false;

  const canCreateIndex = isVideoAgentUnlimited;

  // Video Agent methods
  const canUseChatQuery = async (): Promise<boolean> => {
    if (!currentUser) return false;
    // Unlimited users always have access
    if (isVideoAgentUnlimited) return true;
    
    // For paid users (not unlimited), check credits via canUseVideoAgent
    // For free users, check monthly limits via canUseVideoAgent
    return await BillingService.canUseVideoAgent(currentUser.uid, 'chat');
  };

  const canUseIndexSearch = async (): Promise<boolean> => {
    if (!currentUser) return false;
    if (isVideoAgentUnlimited) return true;
    
    return await BillingService.canUseVideoAgent(currentUser.uid, 'search');
  };

  const consumeChatQuery = async (): Promise<boolean> => {
    if (!currentUser) return false;
    
    return await BillingService.consumeVideoAgentChat(currentUser.uid);
  };

  const consumeSearchQuery = async (): Promise<boolean> => {
    if (!currentUser) return false;
    
    return await BillingService.consumeVideoAgentSearch(currentUser.uid);
  };

  const getVideoAgentUsage = async (): Promise<{ chatQueriesUsed: number; searchQueriesUsed: number }> => {
    if (!currentUser) return { chatQueriesUsed: 0, searchQueriesUsed: 0 };
    
    return await BillingService.getVideoAgentUsage(currentUser.uid);
  };

  const subscriptionStartedAt = userBilling?.subscriptionStartedAt ?? null;

  const value: BillingContextType = {
    userBilling,
    loading,
    error,
    refreshBilling,
    redeemPromoCode,
    consumeCredits,
    upgradeToStarter,
    createPaymentLink,
    cancelSubscription,
    subscriptionStartedAt,
    canRunAnalysis,
    remainingCredits,
    currentPlanDetails,
    // Video Agent
    isVideoAgentUnlimited,
    isPaidUser,
    canCreateIndex,
    canUseChatQuery,
    canUseIndexSearch,
    consumeChatQuery,
    consumeSearchQuery,
    getVideoAgentUsage,
  };

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
};

export default BillingProvider;
